"use client";

import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from "react";
import styles from "./Form.module.css";

/**
 * SubmitButton — standardowy primary button dla submit'u formularza.
 *
 * Brand-styled (tokens z `globals.css`), spójny wygląd między wszystkimi
 * formularzami React-CMS (CContactForm, CUserAccounts, future forms).
 *
 * **Variants:**
 *   - `primary` (default) — solid background `--color-primary`, biały tekst
 *   - `link` — text-only akcja bez tła (np. "Zapomniałeś hasła?", "Anuluj")
 *
 * **Stan loading:** gdy `loading=true`, button pokazuje `loadingLabel` (lub
 * children + "…") i ma `disabled=true`. Eliminuje boilerplate
 * `{busy ? "…" : "Zapisz"}` w każdym komponencie.
 *
 * **A11y:** loading state ustawia `aria-busy=true` żeby screen reader
 * ogłosił że formularz przetwarza.
 *
 * @example
 * ```tsx
 * <SubmitButton loading={isSubmitting}>Wyślij</SubmitButton>
 * <SubmitButton variant="link" type="button" onClick={onCancel}>Anuluj</SubmitButton>
 * ```
 */
export const SubmitButton = forwardRef<HTMLButtonElement, SubmitButtonProps>(
  function SubmitButton(
    {
      variant = "primary",
      loading = false,
      loadingLabel,
      children,
      className,
      disabled,
      type = "submit",
      ...rest
    },
    ref,
  ) {
    const variantClass =
      variant === "link" ? styles.linkButton : styles.primaryButton;
    const isDisabled = disabled || loading;

    return (
      <button
        ref={ref}
        type={type}
        disabled={isDisabled}
        aria-busy={loading || undefined}
        className={[variantClass, className].filter(Boolean).join(" ")}
        {...rest}
      >
        {loading ? (loadingLabel ?? <>{children}…</>) : children}
      </button>
    );
  },
);

export type SubmitButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  /** `primary` (solid background) lub `link` (text-only). Default `primary`. */
  variant?: "primary" | "link";
  /** Stan loading — pokazuje `loadingLabel` (lub `{children}…`), disabled + aria-busy. */
  loading?: boolean;
  /** Custom label podczas `loading`. Default = `{children}…`. */
  loadingLabel?: ReactNode;
};
